import dayjs from 'dayjs'
import { joinURL } from 'ufo'
import type { RouteLocationNormalized } from 'vue-router'
import { defineNuxtPlugin, markIdleBuildReload, markOutdatedBuildReload, reloadNuxtApp, useApmLabels, useCoreConfig, useEventListener, useRouter, useRuntimeConfig, useState } from '#imports'
import { claimReloadFor, fetchLatestBuildId } from '../utils/latestBuild'

// Long enough that whatever the player was typing before leaving the tab, e.g. during a bank app switch,
// is no longer in progress.
const IDLE_RELOAD_AFTER_MS = 15 * 60_000

const isPaymentOrAuth = (route: RouteLocationNormalized): boolean =>
    route.path.startsWith('/payments/') || !!route.query.auth

// Replaces Nuxt's own reaction to a new deploy (`experimental.emitRouteChunkError: 'manual'`), which reloads on
// any navigation. Here a full load only replaces a navigation to another page, or any navigation in playgame
// while no game is open, launching or showing a message. Never one that touches a payment or auth step, or
// that runs while a payment request is in flight.
// A tab that comes back after being hidden for a long time is checked against the deployed build right away,
// before the player acts on it, and reloaded when outdated under the same rules.
// Once the tab knows it is outdated, every APM transaction says so, with what holds the reload back.
export default defineNuxtPlugin({
    name: 'outdated-build-reload',
    setup(nuxtApp) {
        const config = useRuntimeConfig()
        const router = useRouter()
        const reloadEnabled = useCoreConfig().buildReload?.enabled !== false
        const version = String(config.public.apm?.serviceVersion ?? '')
        // Same state keys as usePayments() and usePlaygameState(); read directly so this plugin does not pull in those modules.
        const paymentInFlight = useState<boolean>('payments:processing', () => false)
        const games = useState<Map<string, unknown> | undefined>('playgame-games')
        const startingGame = useState<unknown>('playgame-starting-game')
        const playgameMessage = useState<unknown>('playgame-message')
        const playgame = useState<{ gameStarting?: boolean, paymentsVisible?: boolean } | undefined>('playgame')

        const isPlaygameIdle = (): boolean => !games.value?.size && !startingGame.value && !playgameMessage.value
            && !playgame.value?.gameStarting && !playgame.value?.paymentsVisible

        const blockerOf = (route: RouteLocationNormalized): string | null => {
            if (paymentInFlight.value) return 'payment_in_flight'
            if (route.path.startsWith('/payments/')) return 'payment'
            if (route.query.auth) return 'auth'
            if (route.meta.isPlaygame && !isPlaygameIdle()) return 'game'
            return null
        }

        let outdated = false
        let manifestUnreadable = false

        useApmLabels().register(() => {
            if (!outdated && !manifestUnreadable) return null

            return {
                ...(outdated && {
                    build_outdated: true,
                    build_reload_blocker: reloadEnabled ? blockerOf(router.currentRoute.value) ?? 'none' : 'disabled',
                }),
                ...(manifestUnreadable && { build_check_unreadable: true }),
            }
        })

        let armed = false

        // From here on, the next navigation that may become a full load does.
        const markOutdated = () => {
            outdated = true
            if (armed || !reloadEnabled) return
            armed = true

            router.beforeResolve((to, from) => {
                if (blockerOf(from) || isPaymentOrAuth(to)) return
                if (!from.meta.isPlaygame && to.path === from.path) return

                markOutdatedBuildReload({ path: from.path, version })
                reloadNuxtApp({ path: joinURL(config.app.baseURL, to.fullPath) })
            })
        }

        nuxtApp.hook('app:manifest:update', markOutdated)

        let hiddenAt: number | null = null

        const leave = () => {
            hiddenAt ??= dayjs().valueOf()
        }

        const comeBack = async () => {
            const hiddenFor = hiddenAt === null ? 0 : dayjs().valueOf() - hiddenAt
            hiddenAt = null
            if (hiddenFor < IDLE_RELOAD_AFTER_MS) return

            const latest = await fetchLatestBuildId(config.app)
            manifestUnreadable = !latest
            if (!latest || latest === config.app.buildId) return

            markOutdated()
            const route = router.currentRoute.value
            if (!reloadEnabled || blockerOf(route) || !claimReloadFor(latest)) return

            markIdleBuildReload({ path: route.path, version })
            window.location.reload()
        }

        useEventListener(document, 'visibilitychange', () => {
            if (document.visibilityState === 'hidden') leave()
            else void comeBack()
        })
        useEventListener(window, 'pagehide', leave)
        // A page restored from the back/forward cache resumes where it left off, as a returning tab does.
        useEventListener(window, 'pageshow', (event: PageTransitionEvent) => {
            if (event.persisted) void comeBack()
        })
    },
})
